// crewbit-admin/src/components/money/MoneyExpensesAdmin.tsx
//
// Top-level wrapper for /money/expenses (Phase C screen 5). Composes:
//   PageHeader → NeedsReviewBanner (conditional) → AuthoringExpensesFilterBar
//   → ExpensesTable → MoneyExpenseLogPanel + MoneyExpenseEditPanel
//
// URL state:
//   ?action=log         → opens MoneyExpenseLogPanel
//   ?expense_id={id}    → opens MoneyExpenseEditPanel (Session 125 pattern)
// If both are set, expense_id wins (per Failure mode #7).
//
// Refresh policy: useExpenses owns the fetch + realtime subscription. The
// expenseMutations wrapper-boundary emits MONEY_MUTATION_EVENT after a
// successful record/update; the cross-page useFinancialSummary listener
// catches it. v1 admin Expenses doesn't subscribe to that event directly —
// the realtime subscription on the `expenses` table refreshes the list
// regardless of which surface mutated.
//
// Spec: docs/references/MONEY_MODULE_SPEC_v1.md §Expenses.7.2.

import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, AlertCircle, RefreshCw } from 'lucide-react';
import { PageHeader } from '../common/PageHeader';
import { Skeleton, TableSkeleton } from '../common/Skeleton';
import { admin, palette, radii, shadow, spacing } from '../../styles/tokens';
import { useExpenses } from '../../hooks/useExpenses';
import { useActiveBusinessStore } from '../../hooks/useActiveBusiness';
import { isNeedsReview } from '../../lib/t2125Mapping';
import { AuthoringExpensesFilterBar } from './AuthoringExpensesFilterBar';
import { ExpensesTable } from './ExpensesTable';
import { NeedsReviewBanner } from './NeedsReviewBanner';
import { EmptyExpensesState } from './EmptyExpensesState';
import { MoneyExpenseLogPanel } from './MoneyExpenseLogPanel';
import { MoneyExpenseEditPanel } from './MoneyExpenseEditPanel';
import SettingsGearLink from './settings/SettingsGearLink';
import type { Expense } from '../../types';

const pageStyle: React.CSSProperties = {
  padding: spacing.xl,
  background: admin.bg,
  minHeight: '100vh',
};

export function MoneyExpensesAdmin() {
  const businessId = useActiveBusinessStore((s) => s.activeBusinessId);
  const [searchParams, setSearchParams] = useSearchParams();

  const { expenses, loading, error, refresh, filters, setFilters } = useExpenses(
    businessId ?? undefined,
  );

  const action = searchParams.get('action');
  const expenseIdParam = searchParams.get('expense_id');
  const logOpen = action === 'log' && !expenseIdParam;
  const editId = expenseIdParam;

  const closeLog = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next);
  };
  const closeEdit = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('expense_id');
    setSearchParams(next);
  };
  const openLog = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('expense_id');
    next.set('action', 'log');
    setSearchParams(next);
  };
  const openEdit = (expense: Expense) => {
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    next.set('expense_id', expense.id);
    setSearchParams(next);
  };

  useEffect(() => {
    document.title = 'CrewBit · Expenses';
  }, []);

  // Needs-review count is computed off the unfiltered active set so the banner
  // counts every flagged row even when the table is filtered down.
  const needsReviewCount = useMemo(
    () => expenses.filter(isNeedsReview).length,
    [expenses],
  );

  const newExpenseButton = (
    <button
      type="button"
      onClick={openLog}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 16px',
        borderRadius: radii.sm,
        border: 'none',
        background: palette.accent,
        color: palette.white,
        fontSize: 13,
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      <Plus size={14} strokeWidth={2.25} />
      New expense
    </button>
  );

  const panels = (
    <>
      <MoneyExpenseLogPanel isOpen={logOpen} onClose={closeLog} />
      <MoneyExpenseEditPanel expenseId={editId} onClose={closeEdit} />
    </>
  );

  // Branch 1 — initial load.
  if (loading && expenses.length === 0) {
    return (
      <div style={pageStyle}>
        <PageHeader title="Expenses" back={{ to: '/money', label: 'Money' }} primaryAction={newExpenseButton} secondaryActions={<SettingsGearLink />} />
        <div style={{ marginBottom: spacing.md }}>
          <Skeleton width={320} height={36} />
        </div>
        <div
          style={{
            background: palette.white,
            border: `1px solid ${admin.border}`,
            borderRadius: admin.cardRadius,
            padding: spacing.md,
            boxShadow: shadow.card,
          }}
        >
          <TableSkeleton rows={5} columns={5} />
        </div>
        {panels}
      </div>
    );
  }

  // Branch 2 — initial error.
  if (error && expenses.length === 0) {
    return (
      <div style={pageStyle}>
        <PageHeader title="Expenses" back={{ to: '/money', label: 'Money' }} primaryAction={newExpenseButton} secondaryActions={<SettingsGearLink />} />
        <div
          style={{
            background: palette.amberWash,
            border: `1px solid ${palette.amber}`,
            borderRadius: admin.cardRadius,
            padding: spacing.lg,
            display: 'flex',
            alignItems: 'center',
            gap: spacing.md,
          }}
        >
          <AlertCircle size={20} color={palette.amber} strokeWidth={2} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: palette.amber }}>
              We couldn&apos;t load your expenses.
            </div>
            <div style={{ fontSize: 13, color: palette.amber, marginTop: 4 }}>
              Check your connection and try again.
            </div>
          </div>
          <button
            type="button"
            onClick={refresh}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              borderRadius: radii.sm,
              border: 'none',
              background: palette.amber,
              color: palette.white,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={13} />
            Retry
          </button>
        </div>
        {panels}
      </div>
    );
  }

  // Branch 3 — empty.
  if (expenses.length === 0) {
    return (
      <div style={pageStyle}>
        <PageHeader
          title="Expenses" back={{ to: '/money', label: 'Money' }}
          description="Logged expenses by T2125 line."
          primaryAction={newExpenseButton}
          secondaryActions={<SettingsGearLink />}
        />
        <EmptyExpensesState onLogFirstExpense={openLog} />
        {panels}
      </div>
    );
  }

  // Branch 4 — happy path.
  const description = `${expenses.length} expense${expenses.length === 1 ? '' : 's'} grouped by CRA T2125 line`;
  return (
    <div style={pageStyle}>
      <PageHeader title="Expenses" back={{ to: '/money', label: 'Money' }} description={description} primaryAction={newExpenseButton} secondaryActions={<SettingsGearLink />} />
      <NeedsReviewBanner
        count={needsReviewCount}
        active={!!filters.needsReviewOnly}
        onActivate={() => setFilters({ ...filters, needsReviewOnly: true })}
        onClear={() => setFilters({ ...filters, needsReviewOnly: false })}
      />
      <AuthoringExpensesFilterBar filters={filters} onFiltersChange={setFilters} />
      <ExpensesTable expenses={expenses} onRowClick={openEdit} />
      {panels}
    </div>
  );
}
